import {
  IconButton,
  Spacer,
  SvgChevronDown,
  SvgChevronSmallDown,
  SvgChevronSmallUp,
  SvgChevronUp,
  SvgClose,
  SvgSearch,
  Text,
  TextButton,
  TextInput,
} from '@loomhq/lens';
import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { startVideoSettingsFlow } from 'actions/editor';
import {
  toggleIsTranscriptSearchOpen,
  updateCurrentSearchIndex,
  updateTranscriptSearchInputValue,
  updateTranscriptSearchPhrase,
  updateTranscriptSearchPhraseIndexArray,
} from 'actions/shareVideo';
import { MOBILE_MAX_WIDTH } from 'constants/breakpoints';
import {
  TRANSCRIPTION_SEARCH_CLOSED,
  TRANSCRIPTION_SEARCH_NAVIGATED,
} from 'constants/events';
import { STORE_FILLER_WORD_RANGES } from 'constants/featureFlag';
import { handleToggleTranscriptCreator } from 'creators/shareVideo';
import * as analytics from 'utilities/analytics';
import { isMobile } from 'utilities/device';

import useMedia from 'hooks/useMedia';
import { selectUserIsOwner } from 'selectors/shareVideo';
import {
  selectCurrentSearchIndex,
  selectIsTranscriptSearchOpen,
  selectShowTranscriptToViewer,
  selectTranscriptPhrases,
  selectTranscriptSearchInputValue,
  selectTranscriptSearchPhrase,
  selectTranscriptSearchPhraseIndexArray,
} from 'selectors/transcripts';
import { selectCurrentVideoId } from 'selectors/videoPlayer';

import FillerWordRemoval from './fillerWordRemoval';
import TranscriptHeaderIcons, {
  SEARCH_TRANSCRIPT,
} from '../transcript-header-icons';
import { findIndicesOfSearchPhraseInPhrases } from '../../../../utilities/transcript/findSearchWordInTranscript';

import FeatureFlaggedComponent from 'components/feature-flag-wrapper';

import styles from './styles.module.less';

const TranscriptHeader = ({ onExit, showFullHeader }) => {
  const dispatch = useDispatch();
  const searchTerm = useSelector(selectTranscriptSearchPhrase);
  const searchTermIndices = useSelector(selectTranscriptSearchPhraseIndexArray);
  const inputValue = useSelector(selectTranscriptSearchInputValue);
  const isTranscriptSearchOpen = useSelector(selectIsTranscriptSearchOpen);
  const textInput = useRef(null);
  const transcript = useSelector(selectTranscriptPhrases);
  const phrases = transcript?.phrases;
  const currentSearchIndex = useSelector(selectCurrentSearchIndex);
  const currentUserIsOwner = useSelector(selectUserIsOwner);
  const videoId = useSelector(selectCurrentVideoId);
  const showTranscriptToViewer = useSelector(selectShowTranscriptToViewer);
  const [fillerRemovalDropdownOpen, setFillerRemovalDropdownOpen] = useState(
    false
  );

  const storeUserSearchedPhrase = phrase => {
    dispatch(updateTranscriptSearchInputValue(phrase));

    const trimmedPhrase = phrase.trim();

    if (trimmedPhrase !== searchTerm) {
      dispatch(updateTranscriptSearchPhrase(trimmedPhrase));
    }

    return phrase;
  };

  const closeSearch = useCallback(
    ev => {
      if (isTranscriptSearchOpen && ev.key == 'Escape') {
        dispatch(toggleIsTranscriptSearchOpen(false));
      }
    },
    [isTranscriptSearchOpen, dispatch]
  );

  useEffect(() => {
    if (isTranscriptSearchOpen) {
      textInput.current.focus();
    }

    window.addEventListener('keyup', closeSearch);

    return () => {
      window.removeEventListener('keyup', closeSearch);
    };
  }, [isTranscriptSearchOpen, closeSearch]);

  useEffect(() => {
    if (phrases?.length > 0 && searchTerm.length > 0) {
      const indexArray = findIndicesOfSearchPhraseInPhrases(
        searchTerm,
        phrases
      );

      dispatch(updateTranscriptSearchPhraseIndexArray(indexArray));

      if (indexArray.length > 0) {
        dispatch(updateCurrentSearchIndex(1));
      } else {
        dispatch(updateCurrentSearchIndex(0));
      }
    }
  }, [searchTerm, phrases, dispatch]);

  const incrementSearchIndex = () => {
    if (currentSearchIndex < searchTermIndices.length) {
      dispatch(updateCurrentSearchIndex(currentSearchIndex + 1));
    } else {
      dispatch(updateCurrentSearchIndex(1));
    }

    analytics.track(TRANSCRIPTION_SEARCH_NAVIGATED, {
      currentUserIsOwner,
      video_id: videoId,
    });
  };

  const decrementSearchIndex = () => {
    if (currentSearchIndex > 1) {
      dispatch(updateCurrentSearchIndex(currentSearchIndex - 1));
    } else {
      dispatch(updateCurrentSearchIndex(searchTermIndices.length));
    }

    analytics.track(TRANSCRIPTION_SEARCH_NAVIGATED, {
      currentUserIsOwner,
      video_id: videoId,
    });
  };

  const iconSize = useMedia(
    [`(max-width: ${MOBILE_MAX_WIDTH}px)`],
    ['medium'],
    'small'
  );

  const showFillerWordRemoval =
    !isMobile && !isTranscriptSearchOpen && currentUserIsOwner;

  return (
    <>
      {!isTranscriptSearchOpen && (
        <div className={classNames(styles.header, styles.headerSearchNotOpen)}>
          <div className={styles.headerFirstLine}>
            <Text fontWeight="bold">Transcript beta</Text>
            <div className="flex items:center">
              <TranscriptHeaderIcons />
              <IconButton
                altText="Close"
                size={iconSize}
                icon={<SvgClose />}
                onClick={onExit}
              />
            </div>
          </div>
          {currentUserIsOwner && showTranscriptToViewer === false && (
            <div
              className={classNames([
                styles.visibilityNotice,
                {
                  [styles.scrolled]:
                    !showFullHeader && !fillerRemovalDropdownOpen,
                },
              ])}
            >
              Visible only to you.{' '}
              <button
                className={styles.linkButton}
                onClick={e => {
                  e.preventDefault();
                  dispatch(handleToggleTranscriptCreator());
                  dispatch(startVideoSettingsFlow());
                }}
              >
                Change
              </button>
            </div>
          )}
        </div>
      )}

      {showFillerWordRemoval && (
        <FeatureFlaggedComponent
          flag={STORE_FILLER_WORD_RANGES}
          activationValues={[true]}
        >
          <FillerWordRemoval
            showFullHeader={showFullHeader}
            iconSize={iconSize}
            dropdownOpen={fillerRemovalDropdownOpen}
            setDropdownOpen={setFillerRemovalDropdownOpen}
          />
        </FeatureFlaggedComponent>
      )}

      {isTranscriptSearchOpen && (
        <div
          className={classNames(
            styles.header,
            styles.headerSearchOpen,
            styles.headerFirstLine
          )}
        >
          <div className="flex grow:1">
            <TextInput
              size="small"
              placeholder={SEARCH_TRANSCRIPT}
              icon={<SvgSearch />}
              onChange={ev => {
                storeUserSearchedPhrase(ev.target.value);
              }}
              ref={textInput}
              onKeyDown={ev => {
                if (searchTermIndices.length > 0 && ev.which == 13) {
                  incrementSearchIndex();
                }
              }}
              value={inputValue}
            />
          </div>

          <div className="flex items:center justify:flexEnd ">
            {searchTerm.length > 0 && (
              <>
                <Spacer left={1}>
                  <div className={styles.monospaceText}>
                    <Text>
                      {currentSearchIndex}/{searchTermIndices.length}
                    </Text>
                  </div>
                </Spacer>
                <Spacer left={1}>
                  <div className="flex">
                    <IconButton
                      size={iconSize}
                      altText="Previous Search Term"
                      icon={
                        iconSize === 'small' ? (
                          <SvgChevronSmallUp />
                        ) : (
                          <SvgChevronUp />
                        )
                      }
                      isDisabled={currentSearchIndex === 0}
                      onClick={decrementSearchIndex}
                    />
                    <IconButton
                      size={iconSize}
                      altText="Next Search Term"
                      icon={
                        iconSize === 'small' ? (
                          <SvgChevronSmallDown />
                        ) : (
                          <SvgChevronDown />
                        )
                      }
                      isDisabled={searchTermIndices.length === 0}
                      onClick={incrementSearchIndex}
                    />
                  </div>
                </Spacer>
              </>
            )}
            <Spacer left={0.5}>
              <TextButton
                onClick={() => {
                  dispatch(toggleIsTranscriptSearchOpen(false));

                  analytics.track(TRANSCRIPTION_SEARCH_CLOSED, {
                    currentUserIsOwner,
                    video_id: videoId,
                  });
                }}
              >
                Cancel
              </TextButton>
            </Spacer>
          </div>
        </div>
      )}
    </>
  );
};

export default TranscriptHeader;
